import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Mail, Phone, Building2, Package, ChevronDown } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState({});
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const [form, setForm] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    abn: '',
    address: '',
    payment_terms: 'Net 30',
    notes: '',
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/admin/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(res.data);
      // Fetch product counts per supplier
      try {
        const prodRes = await axios.get(`${API}/admin/products?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const products = prodRes.data.products || [];
        const counts = {};
        products.forEach(p => {
          if (p.supplier_id) {
            if (!counts[p.supplier_id]) counts[p.supplier_id] = [];
            counts[p.supplier_id].push(p);
          }
        });
        setSupplierProducts(counts);
      } catch {}
    } catch (err) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/admin/suppliers/${editingSupplier.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API}/admin/suppliers`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier created');
      }
      setShowModal(false);
      resetForm();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setForm({
      name: supplier.name || '',
      contact_name: supplier.contact_name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      abn: supplier.abn || '',
      address: supplier.address || '',
      payment_terms: supplier.payment_terms || 'Net 30',
      notes: supplier.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to deactivate this supplier?')) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supplier deactivated');
      fetchSuppliers();
    } catch (err) {
      toast.error('Failed to delete supplier');
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setForm({
      name: '', contact_name: '', email: '', phone: '', abn: '', address: '', payment_terms: 'Net 30', notes: ''
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-suppliers">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Suppliers</h1>
            <p className="text-zinc-400 mt-1">Manage your product suppliers</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
            data-testid="add-supplier-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Supplier
          </Button>
        </div>

        {/* Suppliers List */}
        <div className="grid gap-4">
          {loading ? (
            <div className="text-zinc-400">Loading...</div>
          ) : suppliers.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8 text-center">
              <p className="text-zinc-400">No suppliers yet. Add your first supplier to get started.</p>
            </div>
          ) : (
            suppliers.filter(s => s.is_active !== false).map(supplier => (
              <div 
                key={supplier.id} 
                className="bg-zinc-900 border border-zinc-800 rounded-sm p-4"
                data-testid={`supplier-${supplier.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-[#78BE20]" />
                      <h3 className="text-lg font-bold text-white">{supplier.name}</h3>
                      <span className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                        {supplier.payment_terms}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {supplier.contact_name && (
                        <div className="text-zinc-400">
                          <span className="text-zinc-500">Contact:</span> {supplier.contact_name}
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-zinc-400">
                        <Mail className="h-3 w-3" /> {supplier.email}
                      </div>
                      {supplier.phone && (
                        <div className="flex items-center gap-1 text-zinc-400">
                          <Phone className="h-3 w-3" /> {supplier.phone}
                        </div>
                      )}
                      {supplier.abn && (
                        <div className="text-zinc-400">
                          <span className="text-zinc-500">ABN:</span> {supplier.abn}
                        </div>
                      )}
                    </div>
                    {supplier.address && (
                      <p className="mt-2 text-sm text-zinc-500">{supplier.address}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(supplier)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(supplier.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Product count & list */}
                {(() => {
                  const prods = supplierProducts[supplier.id] || [];
                  return (
                    <div className="mt-3 border-t border-zinc-800 pt-3">
                      <button
                        onClick={() => setExpandedSupplier(expandedSupplier === supplier.id ? null : supplier.id)}
                        className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                        data-testid={`toggle-supplier-products-${supplier.id}`}
                      >
                        <Package className="h-3.5 w-3.5" />
                        <span>{prods.length} product{prods.length !== 1 ? 's' : ''} linked</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${expandedSupplier === supplier.id ? 'rotate-180' : ''}`} />
                      </button>
                      {expandedSupplier === supplier.id && prods.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {prods.map(p => (
                            <div key={p.id} className="flex items-center justify-between text-sm bg-zinc-800/50 rounded px-3 py-1.5">
                              <div>
                                <span className="text-white">{p.name}</span>
                                <span className="text-zinc-600 ml-2 font-mono text-xs">{p.sku}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`font-mono text-xs ${p.stock_qty <= (p.low_stock_threshold || 5) ? 'text-red-400' : 'text-zinc-400'}`}>
                                  Stock: {p.stock_qty}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {expandedSupplier === supplier.id && prods.length === 0 && (
                        <p className="mt-2 text-xs text-zinc-600">No products assigned to this supplier. Edit products to link them.</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>ABN</Label>
                  <Input
                    value={form.abn}
                    onChange={(e) => setForm({ ...form, abn: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="COD">COD (Cash on Delivery)</SelectItem>
                      <SelectItem value="Net 7">Net 7</SelectItem>
                      <SelectItem value="Net 14">Net 14</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                  {editingSupplier ? 'Update' : 'Create'} Supplier
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
